// src/components/layouts/CleanMacLayout.tsx
'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { getMenuOptions } from '@/components/menu';

export default function CleanMacLayout({ children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL }: any) {
  const pathname = usePathname();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // 💡 NUEVO: Control menú móvil
  const isActive = (path: string) => pathname === path;

  const menuConfig = getMenuOptions(userRole);
  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL}${config.logo_url}`) : null;
  const companyName = config?.company_name || "Cash Money";

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row p-0 md:p-3 lg:p-5 gap-0 md:gap-5 font-sans">
      
      {/* SIDEBAR ESTILO APP NATIVA (Oscuro y Redondeado) - SOLO DESKTOP */}
      <aside className="hidden md:flex w-64 bg-gray-900 rounded-[2rem] text-white flex-col shadow-2xl overflow-hidden relative shrink-0">
        <div className="flex items-center justify-center h-24 border-b border-white/10 px-4">
          <div className="flex items-center justify-center w-full">
            {logoUrl ? (
              <div className="h-12 flex items-center justify-center">
                <img src={logoUrl} alt={companyName} style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
              </div>
            ) : (
              <span className="text-xl font-bold tracking-wide truncate">{companyName}</span>
            )}
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-4 mt-4 overflow-y-auto scrollbar-thin">
          {menuConfig.map((group, index) => (
            <div key={index} className="space-y-2">
              <div className="px-4 py-1 text-xs font-semibold text-white/50 uppercase tracking-wider">{group.section}</div>
              {group.items.map((item: any, i: number) => (
                <Link key={i} href={item.path} className={`flex items-center space-x-4 p-4 text-sm rounded-2xl transition-all ${isActive(item.path) ? 'bg-white text-gray-900 shadow-lg font-bold scale-105' : 'hover:bg-white/10 text-white/80 font-medium'}`}>
                  <span className="text-xl">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-4 mb-4 mx-4 bg-black/20 rounded-2xl border border-white/5 backdrop-blur-md flex items-center gap-3">
           {userAvatar ? (
             <img src={userAvatar} alt={userName} className="w-10 h-10 rounded-full border border-white/20 object-cover" />
           ) : (
             <div className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center font-bold text-sm">{userInitials}</div>
           )}
           <div className="overflow-hidden">
             <p className="text-[10px] text-white/60 mb-0.5 uppercase tracking-widest">{userRole}</p>
             <p className="font-bold text-xs truncate">{userName}</p>
           </div>
        </div>
      </aside>

      {/* ÁREA DE CONTENIDO PRINCIPAL */}
      <main className="flex-1 bg-white md:rounded-[2rem] shadow-xl flex flex-col overflow-hidden relative min-w-0">
        
        {/* HEADER */}
        <header className="h-16 md:h-20 px-4 md:px-8 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            {/* BOTÓN HAMBURGUESA MÓVIL */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-xl">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} /></svg>
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 tracking-tight truncate">{title}</h1>
          </div>
          
          <div className="relative">
            <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 p-1.5 md:p-2 bg-gray-50 hover:bg-gray-100 rounded-full md:pr-4 transition-colors cursor-pointer border border-gray-200">
              {userAvatar ? (
                 <img src={userAvatar} alt={userName} className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover" />
              ) : (
                 <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-md shadow-inner">{userInitials}</div>
              )}
              <span className="font-semibold text-sm text-gray-700 hidden md:block">Mi Perfil</span>
            </button>
            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-3 w-48 bg-white border border-gray-100 rounded-2xl shadow-2xl py-2 z-50">
                <div className="md:hidden px-4 py-2 border-b border-gray-100 mb-1">
                  <p className="text-sm font-bold text-gray-900 truncate">{userName}</p>
                  <p className="text-[10px] text-gray-500 uppercase">{userRole}</p>
                </div>
                <button onClick={handleLogout} className="w-full text-left block px-5 py-3 text-sm text-red-600 hover:bg-red-50 font-bold transition-colors cursor-pointer">🚪 Cerrar Sesión</button>
              </div>
            )}
          </div>
        </header>

        {/* MENÚ DESPLEGABLE MÓVIL */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 w-full h-[calc(100vh-4rem)] bg-white z-40 overflow-y-auto border-b border-gray-200 shadow-xl">
            <nav className="p-4 space-y-4">
              {menuConfig.map((group, index) => (
                <div key={index} className="space-y-1">
                  <div className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{group.section}</div>
                  {group.items.map((item: any, i: number) => (
                    <Link 
                      key={i} 
                      href={item.path} 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-4 p-3 text-sm rounded-xl transition-all ${isActive(item.path) ? 'bg-gray-900 text-white font-bold' : 'hover:bg-gray-100 text-gray-700 font-medium'}`}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        )}

        <div className="flex-1 p-4 md:p-8 overflow-auto bg-gray-50/30 scrollbar-thin">
          {children}
        </div>
      </main>
    </div>
  );
}